import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import api from '@/api/client'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Search, Shield } from 'lucide-react'
import type { PaginatedResponse, ProductResponse } from '@/api/types'
import { HelpBanner } from '@/components/ui/help-banner'

interface PermissionEntry {
  id: number
  code: string
  description?: string
}

interface PermissionCatalogEntry {
  product_id: number
  permissions: PermissionEntry[]
}

function groupByDomain(permissions: PermissionEntry[]): Record<string, PermissionEntry[]> {
  const groups: Record<string, PermissionEntry[]> = {}
  for (const p of permissions) {
    const parts = p.code.split('.')
    const domain = parts.length >= 2 ? parts[1] : 'other'
    if (!groups[domain]) groups[domain] = []
    groups[domain].push(p)
  }
  return groups
}

function highlightText(text: string, query: string) {
  if (!query) return text
  const idx = text.toLowerCase().indexOf(query.toLowerCase())
  if (idx === -1) return text
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 dark:bg-yellow-800 rounded-sm px-0.5">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function PermissionRow({ perm, highlight, t }: { perm: PermissionEntry; highlight: string; t: (key: string, opts?: any) => string }) {
  const parts = perm.code.split('.')
  const action = parts.slice(2).join('.') || parts[parts.length - 1]
  const isWrite = action.includes('write') || action.includes('delete') || action.includes('approve') || action.includes('execute')
  const isExport = action.includes('export')

  return (
    <div className="flex items-start justify-between py-2 px-3 rounded-md hover:bg-muted/40 transition-colors">
      <div className="flex flex-col gap-0.5 min-w-0">
        <code className="text-xs font-mono bg-muted px-2 py-0.5 rounded w-fit">
          {highlight ? highlightText(perm.code, highlight) : perm.code}
        </code>
        {perm.description && (
          <span className="text-xs text-muted-foreground pl-2">
            {highlight ? highlightText(perm.description, highlight) : perm.description}
          </span>
        )}
      </div>
      <Badge
        variant={isWrite ? 'destructive' : isExport ? 'secondary' : 'outline'}
        className="text-[10px] shrink-0 ml-2 mt-0.5"
      >
        {isWrite ? t('permissionTypes.write', { ns: 'common' }) : isExport ? t('permissionTypes.export', { ns: 'common' }) : t('permissionTypes.read', { ns: 'common' })}
      </Badge>
    </div>
  )
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function DomainSection({ domain, permissions, search, t }: { domain: string; permissions: PermissionEntry[]; search: string; t: (key: string, opts?: any) => string }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1 px-3">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{domain}</span>
        <div className="flex-1 border-t border-dashed" />
        <span className="text-xs text-muted-foreground">{permissions.length}</span>
      </div>
      <div className="flex flex-col">
        {permissions
          .sort((a, b) => a.code.localeCompare(b.code))
          .map((p) => (
            <PermissionRow key={p.id} perm={p} highlight={search} t={t} />
          ))}
      </div>
    </div>
  )
}

export function PermissionsPage() {
  const { t } = useTranslation(['roles', 'common'])
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<number | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['permissions'],
    queryFn: () => api.get<PermissionCatalogEntry[]>('/permissions'),
  })

  const { data: productsData } = useQuery({
    queryKey: ['products-all'],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>('/products?offset=0&limit=100'),
  })

  const productLookup = useMemo(() => {
    const map: Record<number, ProductResponse> = {}
    for (const p of productsData?.data.data ?? []) map[p.id] = p
    return map
  }, [productsData])

  const catalog = data?.data ?? []

  // Auto-select first tab
  const effectiveTab = activeTab ?? catalog[0]?.product_id ?? null

  const filtered = useMemo(() => {
    if (!search.trim()) return catalog
    const q = search.toLowerCase()
    return catalog
      .map((group) => ({
        ...group,
        permissions: group.permissions.filter(
          (p) =>
            p.code.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q)
        ),
      }))
      .filter((group) => group.permissions.length > 0)
  }, [catalog, search])

  const totalPerms = catalog.reduce((s, g) => s + g.permissions.length, 0)
  const filteredPerms = filtered.reduce((s, g) => s + g.permissions.length, 0)

  // When searching, show all matching products; otherwise show active tab
  const isSearching = search.trim().length > 0
  const activeGroup = isSearching ? null : filtered.find((g) => g.product_id === effectiveTab)
  const activeDomains = activeGroup ? groupByDomain(activeGroup.permissions) : null

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="h-6 w-6 text-muted-foreground" />
          <h2 className="text-2xl font-semibold">{t('permissionsCatalog.title')}</h2>
          <Badge variant="secondary" className="text-xs">{t('permissionsCatalog.totalBadge', { count: totalPerms })}</Badge>
        </div>
      </div>

      <HelpBanner storageKey="permissions">
        <p>{t('permissionsCatalog.help.line1')}</p>
        <p>{t('permissionsCatalog.help.line2')}</p>
      </HelpBanner>

      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={t('permissionsCatalog.searchPlaceholder')}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
        {search && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
            {t('permissionsCatalog.matches', { count: filteredPerms })}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
      ) : (
        <>
          {/* Product tabs */}
          {!isSearching && (
            <div className="flex gap-1 border-b mb-4">
              {catalog.map((group) => {
                const product = productLookup[group.product_id]
                const isActive = group.product_id === effectiveTab
                return (
                  <button
                    key={group.product_id}
                    onClick={() => setActiveTab(group.product_id)}
                    className={`px-4 py-2.5 text-sm font-medium transition-colors relative ${
                      isActive
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {product?.name ?? `#${group.product_id}`}
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {group.permissions.length}
                      </Badge>
                    </span>
                    {isActive && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-foreground rounded-full" />
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {/* Tab content (single product) */}
          {!isSearching && activeDomains && (
            <div className="flex flex-col gap-5">
              {Object.keys(activeDomains).sort().map((domain) => (
                <DomainSection
                  key={domain}
                  domain={domain}
                  permissions={activeDomains[domain]}
                  search=""
                  t={t}
                />
              ))}
            </div>
          )}

          {/* Search results (all products) */}
          {isSearching && (
            <div className="flex flex-col gap-6">
              {filtered.map((group) => {
                const product = productLookup[group.product_id]
                const domains = groupByDomain(group.permissions)
                return (
                  <div key={group.product_id}>
                    <div className="flex items-center gap-2 mb-3">
                      <h3 className="text-sm font-semibold">{product?.name ?? `#${group.product_id}`}</h3>
                      <Badge variant="secondary" className="text-[10px]">{product?.code}</Badge>
                      <span className="text-xs text-muted-foreground">{t('permissionsCatalog.matches', { count: group.permissions.length })}</span>
                    </div>
                    <div className="flex flex-col gap-4 pl-1">
                      {Object.keys(domains).sort().map((domain) => (
                        <DomainSection
                          key={domain}
                          domain={domain}
                          permissions={domains[domain]}
                          search={search}
                          t={t}
                        />
                      ))}
                    </div>
                  </div>
                )
              })}
              {filtered.length === 0 && (
                <p className="text-center text-muted-foreground py-8">{t('permissionsCatalog.noSearchResults')}</p>
              )}
            </div>
          )}

          {/* Empty state */}
          {!isSearching && !activeDomains && catalog.length === 0 && (
            <p className="text-center text-muted-foreground py-8">{t('permissionsCatalog.empty')}</p>
          )}
        </>
      )}
    </div>
  )
}
